/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { registry } from '@/lib/page-designer/registry';
import ContentContainer from '@/components/content-container';
import Hero from '@/components/hero';

/**
 * Eagerly register Page Designer components that must NOT lazy-load (UCR-2790).
 *
 * The generated static registry (`static-registry.ts`) registers *every* component as a lazy
 * dynamic import. That's right for most components, but for one that is server-rendered and
 * commonly above the fold it causes a visible flash: on hydration the lazy chunk isn't loaded yet,
 * so `registry.getComponent()` returns null, the component wrapper `throw`s `preload(...)`, and
 * React **discards the server markup and shows the component's `fallback()`** until the chunk
 * arrives (content → fallback → content). React can't help it — a Suspense boundary that re-suspends
 * during hydration always shows its fallback.
 *
 * Registering the component with its already-imported module (`registerComponent`, which sets the
 * entry's `raw` so `getComponent` returns it synchronously) keeps it in the initial module graph, so
 * it's present at hydration and never suspends. `registerImporter` (from the generated registry)
 * merges via `...prev`, so call order vs. `initializeRegistry()` doesn't matter — `raw` wins either
 * way.
 *
 * Trade-off: the eagerly-registered component ships in the initial bundle instead of its own chunk.
 * Reserve this for components that are server-rendered, frequently used, and visibly flash — not as a
 * blanket replacement for lazy loading.
 */
export function registerEagerComponents(targetRegistry = registry): void {
    // Content Container — common, often above the fold; flashed its `fallback()` (tan pulse) on hydration.
    targetRegistry.registerComponent('2_BlackMagic_Assets.contentContainer', ContentContainer);
    // Hero — the top-of-page banner (LCP, above the fold). Eager so an authored hero resolves
    // synchronously and never lazy-flashes on hydration (UCR-2763). The homepage's own hero fallback
    // (HeroFallback) is already a direct import; this covers heroes authored into PD regions.
    targetRegistry.registerComponent('2_BlackMagic_Assets.hero', Hero);
}
